<script lang="ts">
    import { canadaProvincesMap, type Address, addressStore, getResolvedAddresses } from '../../api/address.ts';
    import {
        vvSettings,
        noPictureUrl,
        scrollIntoView,
        extractDistinctStrings,
        countryStrings,
        levelsOfTargetsStrings
    } from '../../api/common.ts';
    import {
        getTargetedMessages,
        possibleCategories,
        type Category,
        type TargetedMessage,
        type PoliticianIdentifier,
        getMatchedTargets,
        onProfileImageError,
        directoryStore
    } from '../../api/officials.ts';
    import { getProfiles, getTextColorByParty, extractPartyFromProfile } from '../../api/elections.ts';
    import * as t from '../../translations/Directory.ts';

    import Button from '../../lib/Button.svelte';
    import SkeletonLoader from '../../lib/SkeletonLoader.svelte';
    
    import CountryFlagCa from '../../icons/CountryFlagCA.svelte';
    import LocalGovernmentIcon from '../../icons/LocalGovernmentIcon.svelte';
    import MediaTargetIcon from '../../icons/MediaTargetIcon.svelte';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';

    import {push} from 'svelte-spa-router'

    let currPrevCountry: string = '';
    let currPrevState: string = '';
    let hasSearched = false;
    let isLoading = false;

    const applicationType = 'advocacy';
    let selectedAddress: Address;
    let officialsElement: Element;

    const hiddenTargetCategories = vvSettings.directory?.hiddenDisplayCategories ?? [];
    if (hiddenTargetCategories.indexOf('media') < 0) {
        hiddenTargetCategories.push('media');
    }
    
    let categories: Category[] = [];
    setCategories($directoryStore.targetedMessages && $directoryStore.targetedMessages.length > 0 ? $directoryStore.targetedMessages[0] : undefined);

    async function _getAndSetTargetAndCategories() {
        if (!$directoryStore.targetedMessages || $directoryStore.targetedMessages.length === 0) {
            $directoryStore.targetedMessages = await getTargetedMessages();
        }
    	const targetedMessages = $directoryStore.targetedMessages;
    	if (!targetedMessages || targetedMessages.length < 1) {
    		categories = [];
    		return;
    	}
    	setCategories(targetedMessages[0]);
    }

    async function _getProfiles(profilesToGet: PoliticianIdentifier[]) {
    	isLoading = true;

    	var profiles = await getProfiles(profilesToGet);
    	isLoading = false;

    	for (let i = 0; i < profiles.length; i++) {
    		const partyAndState = extractPartyFromProfile(profiles[i]);
    		profiles[i].titleAndName = partyAndState.titleAndName.replaceAll('Dear', '');
    		profiles[i].party = partyAndState.party;
    		profiles[i].state = partyAndState.state;
    	}
        
        return profiles;
    }
    
    function setCategories(targetedMessage: TargetedMessage) {
    	categories = [];
    	if (!targetedMessage) return;
    	const distinctTargetCategoriesForAssociation = extractDistinctStrings(targetedMessage.targets.filter((target) => hiddenTargetCategories.indexOf(target.displayCategory) < 0));
    	let assocAllowedCategories = possibleCategories.filter((cat) =>
    		distinctTargetCategoriesForAssociation.some(
    			(tar) =>
    				tar === cat.referenceName &&
    				cat.countries.includes(selectedAddress?.country?.toLocaleLowerCase() || '')
    		)
    	);

    	let groupIds = $directoryStore.matchedTargets.map((target) => target.groupId);
    	let actualTargetCategories = targetedMessage.targets
    		.filter((item) => groupIds.includes(item.groupId))
    		.map((x) => x.displayCategory);
    	categories = assocAllowedCategories.filter((item) =>
    		actualTargetCategories.includes(item.referenceName)
    	);
    }
    function selectFirstCategory() {
        if ($directoryStore.selectedCategory) {
            selectCategory($directoryStore.selectedCategory);
        } else if (categories && categories.length && categories[0]) {
    		selectCategory(categories[0]);
    	}
    }

    async function selectCategory(category: Category) {
    	let identifiers: PoliticianIdentifier[] = [];
    	let groupIds = $directoryStore.targetedMessages[0].targets.filter(
    		(x) => x.displayCategory === category?.referenceName
    	);
    	let currentMatchedTargets = $directoryStore.matchedTargets.filter((x) =>
    		groupIds.some((g) => g.groupId === x.groupId)
    	);

    	for (const matchedTarget of currentMatchedTargets) {
    		for (const match of matchedTarget.matches)
    			identifiers.push({ id: match.id, type: match.type });
    	}

        $directoryStore.selectedCategory = $directoryStore.categories.find(i => i.referenceName === category.referenceName);
        
        if (!$directoryStore.selectedCategory || !$directoryStore.selectedCategory.profiles || $directoryStore.selectedCategory.profiles.length === 0) {
            $directoryStore.selectedCategory = category;
            $directoryStore.selectedCategory.profiles = await _getProfiles(identifiers);

            $directoryStore.categories.push( $directoryStore.selectedCategory);
        }
        
        scrollIntoView(officialsElement);
    }
    
    function getCanadaProvinceMapCode(state: string): string {
    	return canadaProvincesMap[state];
    }

    $: resetOfficials($addressStore.selectedAddress);
    $: resetOfficials($addressStore.addresses);
    
    function resetOfficials() {
        load();
    }
    async function load() {
        setTimeout(() => {
            scrollIntoView(officialsElement);
        }, 100);
        hasSearched = false;
        isLoading = true;
        if ($addressStore.selectedAddress?.checksum) {
            selectedAddress = $addressStore.selectedAddress;
        } else{
            selectedAddress = await getResolvedAddresses(undefined, undefined, applicationType).selectedAddress;
        }
        currPrevCountry = selectedAddress?.country!.toLowerCase();
        currPrevState = selectedAddress?.state!.toLowerCase();

        if ($directoryStore.key !== JSON.stringify(selectedAddress)) {
            $directoryStore.key = '';
            $directoryStore.address = {};
            $directoryStore.matchedTargets = [];
            $directoryStore.targetedMessages = [];
            $directoryStore.categories = [];
            $directoryStore.selectedCategory = undefined;
        }
        
        if (!$directoryStore.matchedTargets || $directoryStore.matchedTargets.length === 0) {
            if (selectedAddress) {
                $directoryStore.matchedTargets = await getMatchedTargets(selectedAddress!);

                $directoryStore.key = JSON.stringify(selectedAddress);
            }
   
            $directoryStore.address = selectedAddress;
        }
        await _getAndSetTargetAndCategories();
        hasSearched = true;
        isLoading = false;

        selectFirstCategory();
    }

    //load();
</script>

{#if hasSearched && !isLoading && categories.length === 0}
<div class="col-span-5">
    <div>
        {t.NoMatchesByAddress_Message()}
    </div>
</div>
{/if}
{#if categories.length || (isLoading && categories.length < 1)}
<div class="flex-col col-span-2 py-2 @xl:border-r">
    {#if categories.length}
        {#each categories as cat}
            <div
                class="flex flex-row pr-6 {(cat.referenceName == levelsOfTargetsStrings.federal ||
                    cat.referenceName == levelsOfTargetsStrings.state) &&
                currPrevCountry == countryStrings.us
                    ? 'py-4'
                    : 'py-8'} gap-2"
            >
                <div class="flex-none w-1/4 my-auto">
                    {#if cat.referenceName == levelsOfTargetsStrings.federal}
                        {#if currPrevCountry == countryStrings.ca}
                            <CountryFlagCa class="h-[35px] mx-auto" />
                        {:else}
                            <div
                                class="!text-[60px] mx-auto
                            vv-state-icon stateface stateface-{currPrevCountry}
                            text-[var(--vv-action-center-association-color,var(--vv-black))]"
                            ></div>
                        {/if}
                    {:else if cat.referenceName == levelsOfTargetsStrings.state}
                        {#if currPrevCountry == countryStrings.ca}
                            <div class="province-container">
                                <div
                                    data-province={currPrevState}
                                    class="text-[var(--vv-action-center-association-color,var(--vv-black))]"
                                >
                                    {getCanadaProvinceMapCode(currPrevState)}
                                </div>
                            </div>
                        {:else}
                            <div
                                class="!text-[60px] mx-auto pl-1.5
                        vv-state-icon stateface stateface-{currPrevState}
                        text-[var(--vv-action-center-association-color,var(--vv-black))]"
                            ></div>
                        {/if}
                    {:else if cat.referenceName == levelsOfTargetsStrings.local}
                        <LocalGovernmentIcon
                            width={48}
                            height={48}
                            class="mx-auto text-[var(--vv-action-center-association-color,var(--vv-black))]"
                        />
                    {:else}
                        <MediaTargetIcon
                            width={48}
                            height={48}
                            class="mx-auto text-[var(--vv-action-center-association-color,var(--vv-black))]"
                        />
                    {/if}
                </div>
                <div class="grow my-auto z-10">
                    <Button
                        type="navigation"
                        class="w-full max-h-[45px] "
                        on:click={() => selectCategory(cat)}
                    >
                        <div class="flex">
                            <div class="grow">{cat.name}</div>
                            <div class="my-auto">
                                <CaretRightIcon color="var(--vv-action-center-button-color)" />
                            </div>
                        </div>
                    </Button>
                </div>
            </div>
            <div class="border-b w-full @xl:w-[90%]"></div>
        {/each}
    {:else if isLoading && categories.length < 1}
        <div>
            <div class="flex flex-row my-8 gap-4 pr-6">
                <SkeletonLoader class="w-1/4" height={48} />
                <SkeletonLoader class="w-full" height={48} />
            </div>

            <div class="border-b w-full @xl:w-[90%]"></div>
            <div class="flex flex-row my-8 gap-4 pr-6">
                <SkeletonLoader class="w-1/4" height={48} />
                <SkeletonLoader class="w-full" height={48} />
            </div>

            <div class="border-b w-full @xl:w-[90%]"></div>
            <div class="flex flex-row my-8 gap-4 pr-6">
                <SkeletonLoader class="w-1/4" height={48} />
                <SkeletonLoader class="w-full" height={48} />
            </div>

            <div class="border-b w-full @xl:w-[90%]"></div>
            <div class="flex flex-row my-8 gap-4 pr-6">
                <SkeletonLoader class="w-1/4" height={48} />
                <SkeletonLoader class="w-full" height={48} />
            </div>
            <div class="border-b w-full @xl:w-[90%]"></div>
        </div>
    {/if}
</div>
{/if}
<div bind:this={officialsElement} class="flex-col col-span-3 p-4 z-10">
{#if $directoryStore.selectedCategory && $directoryStore.selectedCategory?.profiles?.length}
    <div class="text-xl">
        <h2>{$directoryStore.selectedCategory.name || ''}</h2>
        <div class="my-2 text-sm">{$directoryStore.selectedCategory.description || ''}</div>
    </div>
    {#each $directoryStore.selectedCategory.profiles as profile}
        <button
            class="gap-4 flex flex-row w-full flex-nowrap p-4 text-md hover:shadow hover:cursor-pointer"
            on:click={() => push('/Officials/' + profile.id)}
        >
            <div class="w-12 max-w-12">
                {#if isLoading && !$directoryStore.selectedCategory.profiles}
                    <SkeletonLoader class="w-full" height={60} />
                {:else}
                    <img
                        on:error={onProfileImageError}
                        class="rounded rounded-lg w-full"
                        src={profile.photoUrl ?? noPictureUrl}
                        alt={profile.displayName}
                    />
                {/if}
            </div>
            <div class="my-auto text-left flex flex-col">
                <div>
                    {profile.titleAndName}
                </div>
                <div class="text-sm {getTextColorByParty(profile.party ?? '')}">
                    ({profile.party})
                </div>
            </div>
        </button>
    {/each}
{:else if isLoading && (!$directoryStore.selectedCategory || !$directoryStore.selectedCategory?.profiles?.length)}
    <div class="flex flex-col gap-4">
        <div>
            <SkeletonLoader class="w-2/5" height={24} />
        </div>
        <div>
            <SkeletonLoader class="w-[95%]" height={60} />
        </div>
        <div class="flex flex-col gap-4">
            {#each { length: 5 } as _, i}
                <div>
                    <SkeletonLoader class="w-[90%]" height={80} />
                </div>
            {/each}
        </div>
    </div>
{/if}
</div>

<style>
    @import '../../assets/provincely/provincely.css';
    @import '../../assets/stateface/stateface.css';
</style>